/**
 * Feature flag constants.
 *
 * These feature flags are managed in WorkOS and used to control access
 * to various features in the application.
 *
 * Values must match the feature flag names configured in WorkOS.
 */
export const FeatureFlag = {
  // Intake workflow feature flags
  INTAKE_PDF_STAMP: 'intake-pdf-stamp',
  INTAKE_SEE_AI_EXPLANATION: 'intake-see-ai-explanation',
  INTAKE_AI_SCHEDULING: 'intake-ai-scheduling',

  // Medical coding feature flags
  MEDICAL_CODING_ONLY_DOCUMENT_BASED: 'only-document-based-coding',
  // RogerDoc-only (GET /rogerdoc/feature_flags — not configured in WorkOS)
  ALLOW_RERUN_COMPLETED_CODING: 'allow-rerun-completed-coding',
  MEDICAL_CODING_CONSIDER_THERAPY_EVAL: 'medical-coding-consider-therapy-eval',
  MEDICAL_CODING_SHOW_FORCE_CODING_RERUN_BUTTON:
    'medical-coding-show-force-coding-rerun-button',
  MEDICAL_CODING_CHECK_NEW_DOCS_CRON: 'medical_coding_check_new_docs_cron',
  // Reporting feature flags
  ADOPTION_REPORT_BY_BRANCH: 'adoption-report-by-branch',
  ADVANCED_REPORTING: 'advanced-reporting',

  // Admin feature flags
  ADMIN_WORKSHEET_GOALS: 'admin-worksheet-goals',
  // RogerDoc-only: goals progress tracking feature
  USE_WORKSHEET_GOALS_PROGRESS: 'use_worksheet_goals_progress',
  // Billing-config fields (frequency, charge type, PDGM, etc.) in the
  // Add/Edit Insurance popup
  EDIT_INSURANCE_BILLING: 'edit-insurance-billing',
  // Visit type costs configuration in Admin panel
  ADMIN_TAB_BILLING_VIEW: 'admin-tab-billing-view',

  // Order Manager feature flags
  ORDER_MANAGER: 'order-manager',

  // OASIS Manager feature flags
  OASIS_MANAGER: 'oasis-manager',

  // Live Scrubber QA findings — gates the backend `contradictions` response and
  // the QA tab that renders it.
  CONTRADICTION_RULES_ENABLED: 'contradiction-rules-enabled',

  // Show full roles options in the clinician manager UI
  // For non-EMR users they don't need to see the clinic level roles
  SHOW_CLINIC_INFO_IN_USER_MANAGER: 'show-clinic-info-in-user-manager',
  // If true, the user can see roles not assigned to them in the invite/add user dialogs.
  // When false, only roles already assigned to the current user are shown.
  SHOW_UNASSIGNED_ROLES: 'show-unassigned-roles',

  // Product update modal ("What's New") in the app header
  PRODUCT_UPDATE_MODAL_ENABLED: 'product-update-modal-enabled',

  // Payroll Manager screens — Unpaid/Paid tabs (EMR-517). One shared flag
  // gates the sidebar item and every /payroll endpoint.
  PAYROLL_MANAGER: 'payroll-manager',

  // Relaxed billing readiness (EMR-885). On: visits other than eval/re-eval
  // and SOC OASIS count as billing-ready at pending clinician signature, and
  // only SOC OASIS visits must be exported + signed. Mirrors the backend
  // FeatureFlag.PENDING_SIGNATURE_BILLING_READINESS gate.
  PENDING_SIGNATURE_BILLING_READINESS: 'pending-signature-billing-readiness',
} as const;

export const PermissionSlug = {
  NOA_MANAGER: 'noa-manager:view',
  GOALS_ADMIN_TAB: 'goals:admin-tab',
  ORDER_MANAGER: 'order-manager:view',
  OASIS_MANAGER: 'oasis-manager:view',
  CAHPS_MANAGER: 'cahps-manager:view',
  PATIENT_MANAGER: 'patient-mgr:access',
  CLINICIAN_MANAGER: 'clinician-mgr:access',
  REPORTS_ACCESS: 'reports:access',
  ROGER_EMR_REPORTS_ACCESS: 'roger-emr-reports:access',
  REPORTS_VISITS: 'reports:visits',
  REPORTS_EPISODES: 'reports:episodes',
  REPORTS_PATIENTS: 'reports:patients',
  REPORTS_CLAIMS: 'reports:claims',
  // Staff licenses reporting dataset. Independent of user-licenses:view:
  // the report builder never consults the CRUD gate.
  REPORTS_USER_LICENSES: 'reports:user_licenses',
  BILLING_ACCESS: 'billing:view',
  BILLING_EXPORT_CLAIMS: 'billing:export:claims',
  BILLING_EXPORT_REMITTANCES: 'billing:export:remittances',
  BILLING_EXPORT_NOAS: 'billing:export:noas',
  // EMR-1409: send claims outward (submit / send-again / create or abandon a
  // replacement draft). Mirrors the backend CLAIMS_SUBMIT gates on the
  // submission and replacement routes.
  CLAIMS_SUBMIT: 'claims:submit',
  // EMR-1409: attach unmatched 277CA / 835 evidence to a claim version.
  // Mirrors the backend CLAIMS_RECONCILE gate on the reconciliation routes.
  CLAIMS_RECONCILE: 'claims:reconcile',
  QA_ACCESS: 'qa:access',
  HOTBOX_ACCESS: 'hotbox:access',
  HOTBOX_RESCHEDULE: 'hotbox:reschedule',
  EVV_CAPTURE: 'evv:capture',
  EVV_VIEW: 'evv:view',
  EVV_REMOVE: 'evv:remove',
  ADMIN_SUPER_USER: 'admin:super-user',
  EXTERNAL_ADMIN_ACCESS: 'external-admin:access',
  INTAKE_ACCESS: 'intake:access',
  ELIGIBILITY_CHECK: 'eligibility:check',
  CODING_ACCESS: 'coding:access',
  CODING_FORCE_RERUN: 'coding:force-rerun',
  ROGERDOC_DASHBOARD_MANAGE: 'rogerdoc-dashboard:manage',
  CUSTOMIZATIONS_ACCESS: 'customizations:access',
  EMR_MANAGE: 'emr:manage',
  PATIENT_INFO_EDIT: 'patient-info:edit',
  PAYROLL_ACCESS: 'payroll:access',
  // Work log: the per-visit Actions-menu dialog and the Worklog report. The
  // report also needs ROGER_EMR_REPORTS_ACCESS; this slug alone only opens the
  // per-visit dialog.
  WORKLOG_VIEW: 'worklog:view',
  // Amend clinical records inside a closed certification period (EMR-1859).
  // Without it every write on a closed period is refused; the chart reads the
  // slug only to say so before the save, never as the gate.
  EPISODE_AMEND_CLOSED: 'episode:amend-closed',
  // Sees each clinician's scheduling-point load beside their name when
  // assigning work. Separate from the gate that authors those values.
  SCHEDULING_POINTS_VIEW_LOAD: 'scheduling-points:view-load',
  // Staff license tracking. Reading a colleague's credential records, changing
  // them, and authoring the agency's list of credential kinds are three
  // separate acts on two separate screens.
  USER_LICENSES_VIEW: 'user-licenses:view',
  USER_LICENSES_EDIT: 'user-licenses:edit',
  USER_LICENSE_TYPES_EDIT: 'user-license-types:edit',
  // EMR-1442 — one view/edit pair per Admin-tab section, replacing the coarse
  // external-admin:access / emr:manage gate that every section inherited.
  CLINIC_INFO_VIEW: 'clinic-info:view',
  CLINIC_INFO_EDIT: 'clinic-info:edit',
  AGENCY_INSURANCE_VIEW: 'agency-insurance:view',
  AGENCY_INSURANCE_EDIT: 'agency-insurance:edit',
  CLINIC_PHYSICIANS_VIEW: 'clinic-physicians:view',
  CLINIC_PHYSICIANS_EDIT: 'clinic-physicians:edit',
  REFERRAL_SOURCES_VIEW: 'referral-sources:view',
  REFERRAL_SOURCES_EDIT: 'referral-sources:edit',
  CHART_WORKFLOW_VIEW: 'chart-workflow:view',
  CHART_WORKFLOW_EDIT: 'chart-workflow:edit',
  PATIENT_CREATION_SETTINGS_VIEW: 'patient-creation-settings:view',
  PATIENT_CREATION_SETTINGS_EDIT: 'patient-creation-settings:edit',
  // Distinct from SCHEDULING_POINTS_VIEW_LOAD above: that one shows a
  // clinician's accumulated load when assigning work, these gate the table of
  // point values itself.
  SCHEDULING_POINTS_VIEW_CONFIG: 'scheduling-points:view-config',
  SCHEDULING_POINTS_EDIT_CONFIG: 'scheduling-points:edit-config',
  // Authoring the agency's library of standardized text for visit-form text
  // boxes. Only the Admin section is gated; the visit form's own read of the
  // library is authenticated rather than permissioned, so no slug here has
  // anything to do with whether a clinician sees a template.
  FORM_TEMPLATES_VIEW: 'form-templates:view',
  FORM_TEMPLATES_EDIT: 'form-templates:edit',
  // Which task types this agency treats as paperwork rather than covered
  // care. The edit slug decides what reaches a claim, a LUPA count, an
  // authorization budget and a payroll sheet, so it is separate from the
  // scheduling-point values next door.
  TASK_TYPES_VIEW: 'task-types:view',
  TASK_TYPES_EDIT: 'task-types:edit',
} as const;

export type FeatureFlagType = (typeof FeatureFlag)[keyof typeof FeatureFlag];
